import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  generateStablesDepositAddress,
  getStablesApiErrorMessage,
  getStablesDepositAddress,
  getStablesDepositNetworks,
} from "../Services/stablesApi";

export const useStablesDeposit = ({ assetId, networkId, enabled = true } = {}) => {
  const [networks, setNetworks] = useState([]);
  const [addressData, setAddressData] = useState(null);
  const [preferredNetworkId, setPreferredNetworkId] = useState("");
  const [networksLoading, setNetworksLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [networksError, setNetworksError] = useState(null);
  const [error, setError] = useState(null);
  const [generateError, setGenerateError] = useState(null);
  const addressDataRef = useRef(null);
  const preferredNetworkIdRef = useRef("");

  addressDataRef.current = addressData;
  preferredNetworkIdRef.current = preferredNetworkId;

  const loadNetworks = useCallback(async () => {
    setNetworksLoading(true);
    setNetworksError(null);

    try {
      const result = await getStablesDepositNetworks();
      if (!result.length) throw new Error("Empty networks");
      setNetworks(result);
    } catch (err) {
      setNetworks([]);
      setNetworksError(getStablesApiErrorMessage(err, "Não foi possível carregar as redes de depósito."));
    } finally {
      setNetworksLoading(false);
    }
  }, []);

  const loadAddress = useCallback(async () => {
    if (!enabled || !assetId) {
      setAddressData(null);
      setPreferredNetworkId("");
      setError(null);
      return;
    }

    // UI adopted preferred network from the asset resolve — keep existing address payload.
    const cached = addressDataRef.current;
    const preferred = preferredNetworkIdRef.current;
    if (
      networkId &&
      preferred &&
      networkId === preferred &&
      cached?.networkId === networkId &&
      String(cached?.assetId ?? "").toLowerCase() === String(assetId).toLowerCase()
    ) {
      return;
    }

    setLoading(true);
    setError(null);
    // Avoid applying a previous asset's preferred network while resolving.
    if (!networkId) {
      setPreferredNetworkId("");
      setAddressData(null);
    }

    try {
      // Without network_id, MS returns the preferred wallet for the asset
      // (address-first) and fills network_id from that wallet.
      const result = await getStablesDepositAddress(assetId, networkId || undefined);
      setAddressData(result);
      if (!networkId && result?.networkId) {
        setPreferredNetworkId(String(result.networkId).trim().toLowerCase());
      }
    } catch (err) {
      setAddressData(null);
      if (!networkId) setPreferredNetworkId("");
      setError(getStablesApiErrorMessage(err, "Não foi possível carregar o endereço de depósito."));
    } finally {
      setLoading(false);
    }
  }, [assetId, enabled, networkId]);

  useEffect(() => {
    loadNetworks();
  }, [loadNetworks]);

  useEffect(() => {
    loadAddress();
  }, [loadAddress]);

  const availableNetworks = useMemo(() => networks, [networks]);

  const generateAddress = useCallback(async () => {
    if (!assetId || !networkId) return null;

    setGenerating(true);
    setGenerateError(null);

    try {
      const result = await generateStablesDepositAddress(assetId, networkId);
      setAddressData(result);
      setError(null);
      return result;
    } catch (err) {
      const message = getStablesApiErrorMessage(err, "Não foi possível gerar o endereço de depósito.");
      setGenerateError(message);
      return { error: message };
    } finally {
      setGenerating(false);
    }
  }, [assetId, networkId]);

  return {
    networks,
    availableNetworks,
    addressData,
    preferredNetworkId,
    currentAddress: addressData?.address?.trim() || "",
    hasAddress: Boolean(addressData?.hasAddress && addressData?.address?.trim()),
    networksLoading,
    loading,
    generating,
    networksError,
    error,
    generateError,
    reloadNetworks: loadNetworks,
    reloadAddress: loadAddress,
    generateAddress,
  };
};
